import { UserRole } from "@fuse/recoil/pluginStudio/types"

import { ExamplePlugin } from "../types"

export const simpleCounterPlugin: ExamplePlugin = {
  id: "simple-counter",
  name: "Simple Counter",
  description:
    "A basic counter that guests can increment. Perfect for learning the basics.",
  category: "Interactive",
  tags: ["beginner", "counter", "interactive"],
  difficulty: "beginner",
  preview: "A simple button that counts clicks from all guests",
  iconName: "lightning",
  files: {
    [UserRole.GUEST]: `import React, { useState, useEffect } from 'react';

export default function GuestView({ pluginApi, eventId, userId, isLoggedIn }) {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Load initial count from plugin data
  useEffect(() => {
    async function loadData() {
      try {
        const data = await pluginApi.getPluginEventInstanceData();
        setCount(data?.count || 0);
      } catch (error) {
        console.error('Error loading counter data:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const incrementCounter = async () => {
    const newCount = count + 1;
    setCount(newCount);
    
    try {
      await pluginApi.savePluginEventInstanceData({ count: newCount });
    } catch (error) {
      console.error('Error saving counter data:', error);
      // Revert on error
      setCount(count);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-md">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-4 text-center">Event Counter</h2>
      <div className="text-center">
        <div className="text-6xl font-bold text-blue-600 mb-4">{count}</div>
        <button
          onClick={incrementCounter}
          className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Click Me! 🎉
        </button>
      </div>
    </div>
  );
}`,
    [UserRole.HOST_EVENT_VIEW]: `import React, { useState, useEffect } from 'react';

export default function HostView({ pluginApi, eventId, userId, isLoggedIn }) {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Load initial count from plugin data
  useEffect(() => {
    async function loadData() {
      try {
        const data = await pluginApi.getPluginEventInstanceData();
        setCount(data?.count || 0);
      } catch (error) {
        console.error('Error loading counter data:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const resetCounter = async () => {
    setCount(0);
    try {
      await pluginApi.savePluginEventInstanceData({ count: 0 });
    } catch (error) {
      console.error('Error resetting counter:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-md">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-4 text-center">Event Counter (Host View)</h2>
      <div className="text-center">
        <div className="text-6xl font-bold text-blue-600 mb-4">{count}</div>
        <div className="space-x-4">
          <button
            onClick={resetCounter}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
          >
            Reset Counter
          </button>
        </div>
      </div>
    </div>
  );
}`,
    [UserRole.HOST_CONFIG_VIEW]: `import React, { useState, useEffect } from 'react';

export default function HostConfigView({ pluginApi, eventId, userId, isLoggedIn }) {
  const [title, setTitle] = useState('Event Counter');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadConfig() {
      try {
        const config = await pluginApi.getPluginConfig();
        setTitle(config?.title || 'Event Counter');
      } catch (error) {
        console.error('Error loading config:', error);
      } finally {
        setLoading(false);
      }
    }
    loadConfig();
  }, []);

  const saveConfig = async () => {
    try {
      await pluginApi.savePluginConfig({ title });
      alert('Configuration saved!');
    } catch (error) {
      console.error('Error saving config:', error);
      alert('Error saving configuration');
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-md">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-4">Counter Configuration</h2>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Counter Title
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Enter counter title"
        />
      </div>

      <button
        onClick={saveConfig}
        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
      >
        Save Configuration
      </button>
    </div>
  );
}`,
  },
}
